import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dropdown } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { getYears, getMonths, getDayList } from '../../pets/functions';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { Footer } from '../Footer';
import { Question } from '../Question';
import { StyledForm } from '../StyledForm';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted } from '../events';
import { StyledDropdownGroup } from './styles';
import { schema, optionalSchema } from './schema';

export { schema, optionalSchema } from './schema';

export const BirthdayForm = ({
  labelYear,
  labelMonth,
  labelDay,
  onSubmit,
  defaultValues,
  goBack,
  index,
  pupBirthdayQuestionCaption,
  pupBirthdayQuestionPrompt,
  pupBirthdayQuestionSubheadline,
  ctaButtonText,
  showDateInput = true,
  isOptional = false,
}) => {
  const [optionsYear, setOptionsYear] = useState([]);
  const [optionsMonth, setOptionsMonth] = useState([]);
  const [optionsDay, setOptionsDay] = useState([]);
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitted, isSubmitSuccessful },
    getValues,
    reset,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(isOptional ? optionalSchema : schema),
  });

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const refreshYearList = useCallback(() => {
    const year = getYears();
    setOptionsYear(year);
  }, []);

  const refreshMonthList = useCallback(() => {
    const month = getMonths(getValues('year'));
    setOptionsMonth(month);
  }, [getValues]);

  const refreshDayList = useCallback(() => {
    const data = getDayList(getValues('year'), getValues('month'), labelDay);
    setOptionsDay(data);
  }, [getValues, labelDay]);

  useEffect(() => {
    refreshYearList();
    refreshMonthList();
    refreshDayList();
  }, [refreshDayList, refreshMonthList, refreshYearList]);

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(index, 'dog_birthday', cart?.customer || customer),
    );
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        <Question
          prompt={pupBirthdayQuestionPrompt}
          caption={pupBirthdayQuestionCaption}
          subheadline={pupBirthdayQuestionSubheadline}
        >
          <StyledDropdownGroup>
            <Controller
              name="year"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  showLabel={false}
                  noSelectionLabel={labelYear}
                  label={labelYear}
                  onChange={e => {
                    field.onChange(e);
                    refreshMonthList();
                  }}
                  selected={field?.value ? field.value.toString() : ''}
                  error={error?.message}
                  options={optionsYear}
                />
              )}
            />
            <Controller
              name="month"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  showLabel={false}
                  noSelectionLabel={labelMonth}
                  label={labelMonth}
                  onChange={e => {
                    field.onChange(e);
                    refreshDayList();
                    // trigger('year'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                  }}
                  disabled={optionsMonth.length === 0}
                  selected={field?.value ? field.value.toString() : ''}
                  error={error?.message}
                  options={optionsMonth}
                />
              )}
            />
            {showDateInput && (
              <Controller
                name="day"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => {
                  let selected = field?.value ? field.value.toString() : '';
                  if (selected.length === 1) selected = `0${selected}`; // This for some reason became necessary as part of this ticket https://ollie.atlassian.net/browse/ES-897
                  return (
                    <Dropdown
                      {...field}
                      showLabel={false}
                      noSelectionLabel={labelDay}
                      label={labelDay}
                      onChange={val => {
                        field.onChange(val);
                        // trigger('month'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                      }}
                      disabled={optionsDay.length === 0}
                      selected={selected}
                      error={error?.message}
                      options={optionsDay}
                    />
                  );
                }}
              />
            )}
          </StyledDropdownGroup>
        </Question>
      </FormFieldsContainer>
      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          !isValid || isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        onBackButtonClick={goBack}
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
      />
    </StyledForm>
  );
};

BirthdayForm.displayName = 'BirthdayForm';

BirthdayForm.propTypes = {
  labelYear: PropTypes.string.isRequired,
  labelMonth: PropTypes.string.isRequired,
  labelDay: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  goBack: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({}).isRequired,
  pupBirthdayQuestionCaption: PropTypes.string.isRequired,
  pupBirthdayQuestionPrompt: PropTypes.string.isRequired,
  pupBirthdaySubheadline: PropTypes.string,
  ctaButtonText: PropTypes.object,
  showDate: PropTypes.bool,
};
